import { Object } from '../Object/Object'
import { is } from '../is/is'

import { wait } from '../utils/utils'

import type {
  TSequence, IAddParams, TAllAudios, IPlayParams,
  TAudioFormat, ISoundSystem, TPlayingAudios,
  TSoundTestResult, TPossibleAudioData, TPossiblePlayingAudios
} from './definitions'

const DEFAULT_SEQUENCE_DELAY = 0

export default abstract class BaseSoundSystem<
  T extends TPossibleAudioData,
  K extends TPossiblePlayingAudios,
  F extends IAddParams = IAddParams
>
implements ISoundSystem<F> {
  protected allPlayingAudios: TPlayingAudios<K> =  {}

  // Be- vagy kikapcsolt állatpoban van a hangrendszer
  protected isEnabled = true

  // Elindítottunk egy szekvenciát.
  protected isPlayingSequence = false

  // Van-e hangtámogatás
  protected audioFormat: TSoundTestResult = false

  // Ebbe töltjük típusonként az Audio elemeket
  protected readonly allAudios: TAllAudios<T> = { mp3: {}, ogg: {} }

  public get audioKeys (): readonly string[] {
    const saf = this.supportedAudioFormat

    if (!saf) {
      return []
    }

    return Object.getKeys(this.allAudios[saf])
  }

  public set supportedAudioFormat (saf: TSoundTestResult) {
    this.audioFormat = saf
  }

  public get supportedAudioFormat (): TSoundTestResult {
    return this.audioFormat
  }

  // Ki tudjuk kapcsolni az ss-t kívülről is
  public set enabled (isEnabled: boolean) {
    this.isEnabled = isEnabled
  }

  public get enabled (): boolean {
    return this.isEnabled
  }

  public constructor () {
    /* eslint-disable no-restricted-syntax -- Itt engedjük a globális focust és blurt. */
    window.addEventListener('focus', this.onFocus)
    window.addEventListener('blur', this.onBlur)
    /* eslint-enable no-restricted-syntax */
  }

  /** Van hangtámogatás. */
  public canPlayAudio (): boolean {
    return is.string(this.supportedAudioFormat)
  }

  /**
   * Hangfájl hozzáadása.
   * !!! Az ogg csak lehetséges későbbre, foglalkozzunk az mp3-al.
   * @param params - Params.
   */
  public async add (params: F): Promise<void> {
    if (this.canPlayAudio()) {
      await this.addAudio(params)
    }
  }

  /**
   * Hang lejátszása.
   * @see {@link https://developers.google.com/web/updates/2017/09/autoplay-policy-changes}
   * @param params - Params.
   */
  public play (params: IPlayParams): void | number {
    const { onEnd } = params

    // A user kikapcsolta a hangokat, vagy teszt környezet.
    if (!this.enabled) {
      if (onEnd) {
        onEnd()
      }

      return
    }

    const saf = this.supportedAudioFormat

    // Hogy ne hasaljon el a játék a promise-nál, ha nincs audio támogatás, akkor egyből resolve
    if (!saf) {
      if (onEnd) {
        onEnd()
      }

      return
    }

    return this.playAudio(params, saf)
  }

  /**
   * Hangszekvencia lejátszása.
   * @param keys    - Szekvenciában szereplő hangfájlok kulcsai.
   * @param delay   - A hangok lejátszása közötti kivárás.
   * @param isForce - Egy hangot, egymásután gyorsan le tudjunk játszani.
   * @param volume  - Hangerő.
   */
  public playSequence ( // eslint-disable-line @typescript-eslint/max-params -- Legacy.
    keys: TSequence,
    delay: number = DEFAULT_SEQUENCE_DELAY,
    isForce = false,
    volume?: number
  ): Promise<void> {
    const sequence: string[] = [ ...keys ]

    this.isPlayingSequence = true

    return new Promise((resolve, reject) => {
      /**
       * Hangok lejátszása. Az utolsó lejátszandó hangig hívja magát.
       * @param remainingKeys - Még hátralévő/lejátszandó hangfájlok kulcsai.
       */
      const playKeys = ( // eslint-disable-line no-restricted-syntax -- this miatt kell az arrow.
        remainingKeys: string[]
      ): void => {
        if (is.empty(remainingKeys)) {
          this.isPlayingSequence = false

          resolve()

          return
        }

        const key = sequence.shift()

        if (is.string(key)) {
          if (this.isPlayingSequence) {
            this.play({
              key, isForce, volume, onEnd: async () => {
                if (is.empty(remainingKeys)) {
                  this.isPlayingSequence = false

                  resolve()
                }
                else {
                  if (delay > 0) {
                    await this.wait(delay)
                  }

                  playKeys(remainingKeys)
                }
              }
            })
          }
          else {
            reject(new Error('a szekvencia meg lett szakítva.'))
          }
        }
      }

      playKeys(sequence)
    })
  }

  /** Összes hang leállítása. */
  public mustKillSound (): void {
    this.isPlayingSequence = false

    Object.getKeys(this.allPlayingAudios).forEach((key) => {
      this.stop(key)
    })
  }

  /**
   * Szerepel az allPlayingAudios között?.
   * @param key - A hangfájl kulcsa.
   */
  public isPlaying (key: string): boolean {
    const audio = this.allPlayingAudios[key]

    return is.defined(audio)
  }

  /**
   * Wait/SetTimeout.
   * @param duration - Várkozni kívánt idő (ms) hossza.
   */
  protected wait (
    duration: number
  ): Promise<void> {
    return wait(duration)
  }

  /** Az összes hang „mútolása”. */
  private readonly onBlur = (): void => {
    this.mute()
  }

  /** Az összes hang „demútolása”. */
  private readonly onFocus = (): void => {
    this.unmute()
  }

  /**
   * Hang pauzálása.
   * @param key - A hangfájl kulcsa.
   */
  public abstract pause (key: string): void

  /**
   * Hangfájl lejátszásának folytatása.
   * @param key - A hangfájl kulcsa.
   */
  public abstract resume (key: string): void

  /**
   * Hang lejátszásának megállítása.
   * @param key - A hangfájl kulcsa.
   */
  public abstract stop (key: string): void

  /**
   * Hang hangerejének beállítása.
   * @param key    - A hangfájl kulcsa.
   * @param volume - Hangerő.
   */
  public abstract setAudioVolume (key: string, volume: number): void

  /**
   * Hang hangerejének beállítása.
   * @param key - A hangfájl kulcsa.
   */
  public abstract getAudioVolume (key: string): number

  /**
   * Szerepel az allPlayingAudios és szüneteltetve van a lejátszás?
   * @param key - A hangfájl kulcsa.
   * @param id  - Hanglejátszás id-ja.
   */
  public abstract isPaused (key: string, id: number): boolean

  protected abstract addAudio (params: F): Promise<void>

  protected abstract playAudio (params: IPlayParams, saf: TAudioFormat): void | number

  protected abstract mute (): void

  protected abstract unmute (): void
}
